// src/pages/ClosedActivities.jsx
import React, { useEffect, useState, useContext } from "react";
import Search from "../../components/Search";
import FilterDropDown from "../../components/FilterDropDown";
import CreateActivityButton from "../../components/buttons/CreateActivityButton";
import UserBadge from "../../components/buttons/UserBadge";
import api, { getUploadUrl } from "../../Api";
import iconId from "../../assets/icon/arrow/freccia.png";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import icon1 from "../../assets/icon/pin/Pin/pinArancione.png";
import icon2 from "../../assets/icon/pin/Pin/pinBlu.png";
import icon3 from "../../assets/icon/pin/Pin/pinGiallo.png";
import icon4 from "../../assets/icon/pin/Pin/pinRosa.png";
import icon5 from "../../assets/icon/pin/Pin/pinTurchese.png";
import icon6 from "../../assets/icon/pin/Pin/pinVerde.png";
import icon7 from "../../assets/icon/pin/Pin/pinViola.png";
import icon8 from "../../assets/icon/pin/Pin/PinRosso.png";
import BackButton from "../../components/buttons/BackButton";
import { UserContext } from "../../context/UserContext";

import * as XLSX from "xlsx";       // <— usato per l’export Excel
import JSZip from "jszip";          // <— usato per lo ZIP foto

export default function ClosedActivities() {
  const { user } = useContext(UserContext);
  const isRup = user?.role === "rup";           // <— flag di comodo

  const [activities, setActivities] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const itemsPerPage = 20;
  const navigate = useNavigate();

  /* ------------------------------------------------------------------ */
  /*                                DATA                                */
  /* ------------------------------------------------------------------ */

  const fetchActivities = async () => {
    try {
      let endpoint, params;
      if (isRup) {
        endpoint = "/activity/worksite-activities";
        params   = { status: "closed", worksite: user.worksite };
      } else {
        endpoint = "/activity/activities";
        params   = { status: "closed" };
      }

      const res       = await api.get(endpoint, { params });
      const userCache = {};

      const enriched = await Promise.all(
        res.data.map(async act => {
          let operatorName = "N/A";
          let role         = "-";

          if (act.operator_id) {
            if (userCache[act.operator_id]) {
              const cached = userCache[act.operator_id];
              operatorName = `${cached.firstName} ${cached.lastName}`;
              role         = cached.role || "-";
            } else {
              try {
                const u = (await api.get(`/user/${act.operator_id}`)).data;
                userCache[act.operator_id] = u;
                operatorName = `${u.firstName} ${u.lastName}`;
                role         = u.role || "-";
              } catch { /* noop */ }
            }
          }

          return { ...act, operatorName, role };
        })
      );

      setActivities(enriched);
    } catch (err) {
      console.error("Errore nel caricamento attività:", err);
    }
  };

  useEffect(() => { if (user) fetchActivities(); }, [user]);
  useEffect(() => { setCurrentPage(1); }, [searchQuery, activeFilters]);

  /* ------------------------------------------------------------------ */
  /*                         FORMATTING HELPERS                         */
  /* ------------------------------------------------------------------ */

  const capitalizeFirst = str => str.charAt(0).toUpperCase() + str.slice(1);

  const formatDateWithWeekday = dateStr => {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const icons = {
    Potatura: icon1, Abbattimento: icon2, "Reti di recinzione": icon3,
    Sfalcio: icon4,  "Reti paramassi": icon5, Diserbo: icon6,
    "Censimento arboreo": icon7, "Pulizia strada": icon8
  };

  const labelMap = {
    potatura: "Potatura", abbattimento: "Abbattimento",
    reti_recinzione: "Reti di recinzione", sfalcio: "Sfalcio",
    reti_paramassi: "Reti paramassi", diserbo: "Diserbo",
    censimento_arboreo: "Censimento arboreo", pulizia_strada: "Pulizia strada"
  };

  const getActivityIcon   = label => icons[label] || null;
  const getLabelFromValue = val   => labelMap[val] || val;

  const formatLocation = (loc, actName) => {
    if (!loc) return "-";
    const parts = loc.split(",").map(p => p.trim());
    const icon  = getActivityIcon(getLabelFromValue(actName));
    return (
      <span className="flex items-center gap-2">
        {icon && <img src={icon} alt="" className="w-5" />}
        <span>{`${parts[1] || ""} ${parts[0] || ""}, ${parts[4] || ""}`}</span>
      </span>
    );
  };

  /* ------------------------------------------------------------------ */
  /*                            FILTER / PAGE                           */
  /* ------------------------------------------------------------------ */

  const filteredActivities = activities.filter(act => {
    const matchesTipo =
      activeFilters.length === 0 ||
      act.activity_names?.some(name =>
        activeFilters.includes(name.toLowerCase().replace(/\s/g, "_"))
      );

    const matchesSearch =
      searchQuery.trim() === "" ||
      (act.location_info &&
        act.location_info.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesTipo && matchesSearch;
  });

  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);
  const paginatedActivities = filteredActivities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  /* ------------------------------------------------------------------ */
  /*                               EXPORT                               */
  /* ------------------------------------------------------------------ */

  const buildExportRows = act => {
    const beforeUrls = Array.isArray(act.photo_before)
      ? act.photo_before.map(id => getUploadUrl(id))
      : act.photo_before ? [getUploadUrl(act.photo_before)] : [];

    const afterUrls  = Array.isArray(act.photo_after)
      ? act.photo_after.map(id => getUploadUrl(id))
      : act.photo_after ? [getUploadUrl(act.photo_after)] : [];

    if (isRup) {
      return [
        act.operatorName,
        act.role,
        act.activity_names.join("; "),
        act.location_info,
        beforeUrls.join("; "),
        afterUrls.join("; "),
      ];
    }

    return [
      formatDateWithWeekday(act.end_date),
      act.operatorName,
      act.role,
      act.activity_names.join("; "),
      act.location_info,
      beforeUrls.join("; "),
      afterUrls.join("; "),
    ];
  };

  const downloadCSV = () => {
    const headers = isRup
      ? ["Responsabile", "Ruolo", "Attività", "Location", "Foto Prima", "Foto Dopo"]
      : ["Data lavoro", "Responsabile", "Ruolo", "Attività", "Location", "Foto Prima", "Foto Dopo"];

    const rows = filteredActivities.map(buildExportRows);

    const csv = [headers, ...rows]
      .map(r => r.map(f => `"${String(f).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href      = url;
    a.download   = "closed_activities_report.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadExcel = () => {
    const data = filteredActivities.map(act => {
      const beforeUrls = Array.isArray(act.photo_before)
        ? act.photo_before.map(id => getUploadUrl(id)).join("; ")
        : act.photo_before ? getUploadUrl(act.photo_before) : "";

      const afterUrls  = Array.isArray(act.photo_after)
        ? act.photo_after.map(id => getUploadUrl(id)).join("; ")
        : act.photo_after ? getUploadUrl(act.photo_after) : "";

      return isRup
        ? {
            Responsabile: act.operatorName,
            Ruolo:        act.role,
            Attività:     act.activity_names.join("; "),
            Location:     act.location_info,
            "Foto Prima": beforeUrls,
            "Foto Dopo":  afterUrls,
          }
        : {
            "Data lavoro": formatDateWithWeekday(act.end_date),
            Responsabile:  act.operatorName,
            Ruolo:         act.role,
            Attività:      act.activity_names.join("; "),
            Location:      act.location_info,
            "Foto Prima":  beforeUrls,
            "Foto Dopo":   afterUrls,
          };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attività Chiuse");
    XLSX.writeFile(wb, "closed_activities_report.xlsx");
  };

  /* ------------------------------------------------------------------ */
  /*                            PHOTO ZIP / UI                          */
  /* ------------------------------------------------------------------ */

  const toggleSelect = id =>
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const downloadSelectedPhotos = async () => {
    if (selectedIds.length === 0) {
      toast.error("Seleziona almeno un'attività.");
      return;
    }
    const zip           = new JSZip();
    const fetchPromises = [];

    selectedIds.forEach(actId => {
      const act = activities.find(a => a.id === actId);
      if (!act) return;

      const collect = (ids, prefix) => {
        (Array.isArray(ids) ? ids : ids ? [ids] : []).forEach((pid, idx) => {
          const url = getUploadUrl(pid);
          const ext = url.split(".").pop().split(/[#?]/)[0];
          const name = `${actId}_${prefix}_${idx + 1}.${ext}`;
          fetchPromises.push(
            fetch(url)
              .then(res => res.arrayBuffer())
              .then(data => zip.file(name, data))
              .catch(() => console.warn(`Impossibile scaricare ${url}`))
          );
        });
      };

      collect(act.photo_before, "prima");
      collect(act.photo_after,  "dopo");
    });

    await Promise.all(fetchPromises);
    zip.generateAsync({ type: "blob" }).then(content => {
      const blobUrl = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href  = blobUrl;
      a.download = "selected_photos.zip";
      a.click();
      URL.revokeObjectURL(blobUrl);
    });
  };

  const openPhotosInGallery = urls =>
    urls.forEach(url => window.open(url, "_blank", "noopener,noreferrer"));

  /* ------------------------------------------------------------------ */
  /*                                RENDER                              */
  /* ------------------------------------------------------------------ */

  return (
    <div className="min-h-screen bg-global p-12">
      {/* Header */}
      <header className="flex justify-between items-center mb-6">
        <div className="flex space-x-2 items-center">
          <FilterDropDown
            options={Object.entries(labelMap).map(([value, label]) => ({ value, label }))}
            onFilterChange={setActiveFilters}
          />
          <Search onSearch={setSearchQuery} />
        </div>
        <div className="flex space-x-2 items-center">
          <CreateActivityButton />
          <UserBadge />
          <BackButton />
        </div>
      </header>

      {/* Export buttons */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={downloadCSV}
          className="px-4 py-2 rounded-xl bg-primary border-2 border-secondary text-white"
        >
          Scarica CSV Report
        </button>
        <button
          onClick={downloadExcel}
          className="px-4 py-2 rounded-xl bg-primary border-2 border-secondary text-white"
        >
          Scarica Excel Report
        </button>
        <button
          onClick={downloadSelectedPhotos}
          className="px-4 py-2 rounded-xl bg-secondary border-2 border-primary text-white"
        >
          Scarica Foto Selezionate
        </button>
      </div>

      {/* Table */}
      <div className="mt-8">
        <h3 className="text-white text-xl font-semibold mb-4">Attività Chiuse</h3>
        <div className="w-full overflow-x-auto rounded-2xl">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-gray-400 uppercase text-xs tracking-wider">
                <th className="py-2 px-4">Sel</th>
                <th className="py-2 px-4">Data lavoro</th>
                <th className="py-2 px-4">Responsabile</th>
                <th className="py-2 px-4">Ruolo</th>
                <th className="py-2 px-4">Attività</th>
                <th className="py-2 px-4">Location</th>
                <th className="py-2 px-4">Foto Prima</th>
                <th className="py-2 px-4">Foto Dopo</th>
                <th className="py-2 px-4">Dettaglio</th>
              </tr>
            </thead>
            <tbody>
              {paginatedActivities.map((act, idx) => {
                const bg = idx % 2 === 0 ? "bg-global" : "bg-primary";
                const formattedDate = formatDateWithWeekday(act.end_date);

                const beforeUrls = Array.isArray(act.photo_before)
                  ? act.photo_before.map(id => getUploadUrl(id))
                  : act.photo_before ? [getUploadUrl(act.photo_before)] : [];

                const afterUrls = Array.isArray(act.photo_after)
                  ? act.photo_after.map(id => getUploadUrl(id))
                  : act.photo_after ? [getUploadUrl(act.photo_after)] : [];

                return (
                  <tr key={act.id} className={`${bg} text-white hover:bg-white/5`}>
                    <td className="py-6 px-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(act.id)}
                        onChange={() => toggleSelect(act.id)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="py-6 px-4">{formattedDate}</td>
                    <td className="py-6 px-4">{act.operatorName}</td>
                    <td className="py-6 px-4">
                      <span className="border-2 border-secondary rounded-full p-2 px-6">
                        {act.role}
                      </span>
                    </td>
                    <td className="py-6 px-4">
                      <div className="flex flex-col gap-2">
                        {act.activity_names.map((name, i) => {
                          const label = getLabelFromValue(name);
                          const icon  = getActivityIcon(label);
                          return (
                            <div key={i} className="flex items-center gap-4">
                              {icon && <img src={icon} alt={label} className="w-4" />}
                              <span className="text-sm">{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="py-6 px-4">
                      {formatLocation(act.location_info, act.activity_names?.[0])}
                    </td>
                    <td className="py-6 px-4">
                      {beforeUrls.length > 0 ? (
                        <button
                          onClick={() => openPhotosInGallery(beforeUrls)}
                          className="px-3 py-1 rounded bg-secondary text-white hover:bg-secondary/80 text-sm"
                        >
                          Visualizza {beforeUrls.length} Foto
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-6 px-4">
                      {afterUrls.length > 0 ? (
                        <button
                          onClick={() => openPhotosInGallery(afterUrls)}
                          className="px-3 py-1 rounded bg-secondary text-white hover:bg-secondary/80 text-sm"
                        >
                          Visualizza {afterUrls.length} Foto
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-6 px-4">
                      <img
                        src={iconId}
                        alt="Dettaglio attività"
                        onClick={() => navigate(`/activity-details/${act.id}`)}
                        className="w-5 h-5 opacity-80 hover:opacity-100 cursor-pointer"
                      />
                    </td>
                  </tr>
                );
              })}
              {paginatedActivities.length === 0 && (
                <tr className="bg-global text-white text-center">
                  <td colSpan="9" className="py-6 px-4">
                    Nessuna attività chiusa corrispondente ai criteri.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6 gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded bg-secondary text-white disabled:opacity-50"
            >
              ← Precedente
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-4 py-2 rounded ${
                  currentPage === i + 1
                    ? "bg-white text-black"
                    : "bg-gray-700 text-white"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded bg-secondary text-white disabled:opacity-50"
            >
              Successiva →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
